import {
  Badge,
  createDataTableColumnHelper,
  DataTable,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Link,
} from "@beweegbaas/ui";
import { createFileRoute, stripSearchParams } from "@tanstack/react-router";
import { useRouter } from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { functionalUpdate, type OnChangeFn, type SortingState } from "@tanstack/react-table";
import { useMemo } from "react";
import { useDateFormatter } from "#/hooks/use-date-formatter";
import { toast } from "@beweegbaas/ui";
import { DataTableActions } from "#/components/data-table-actions";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { Page } from "#/components/page";
import { PendingContent } from "#/components/pending-content";
import { CreateRapportButton } from "#/modules/rapporten/components/button.create-rapport";
import { getReportsOverview, submitDeleteRapport } from "#/modules/rapporten/rapporten.functions";
import {
  RAPPORTEN_OVERVIEW_SEARCH_DEFAULTS,
  rapportenOverviewSearchSchema,
} from "#/modules/rapporten/params.rapporten";
import { m } from "#/paraglide/messages";

const RAPPORTEN_OVERVIEW_PAGE_SIZE = 1000;

export const Route = createFileRoute("/$locale/{-$schoolyear}/_authenticated/$school/reports/")({
  validateSearch: rapportenOverviewSearchSchema,
  // Keeps default sort out of the URL.
  search: { middlewares: [stripSearchParams(RAPPORTEN_OVERVIEW_SEARCH_DEFAULTS)] },
  // Narrowed to exactly the keys the loader/server-fn uses — never the whole search object.
  loaderDeps: ({ search }) => ({ orderBy: search.orderBy, orderDirection: search.orderDirection }),
  loader: ({ deps }) => getReportsOverview({ data: deps }),
  component: ReportsIndexPage,
  errorComponent: ReportsIndexError,
});

type RapportRow = Awaited<ReturnType<typeof getReportsOverview>>["rapporten"][number];

const helper = createDataTableColumnHelper<RapportRow>();

// Rapporten with klassen deep-link into step 3 of the flow; the rest resume at step 2.
function rapportLink(
  row: RapportRow,
  params: { locale: string; schoolyear?: string; school: string },
) {
  const firstKlasId = row.klassen[0]?.id;
  return firstKlasId
    ? {
        to: "/$locale/{-$schoolyear}/$school/reports/flow/$id/step/3/$class",
        params: { ...params, id: row.id, class: firstKlasId },
      }
    : {
        to: "/$locale/{-$schoolyear}/$school/reports/flow/$id/step/2",
        params: { ...params, id: row.id },
      };
}

function ReportsIndexPage() {
  const { locale, schoolyear, school } = Route.useParams();
  const { rapporten, totalCount } = Route.useLoaderData();
  const router = useRouter();
  const submitDeleteRapportFn = useServerFn(submitDeleteRapport);
  const { orderBy, orderDirection } = Route.useSearch();
  const navigate = Route.useNavigate();

  const sorting: SortingState = orderBy
    ? [{ id: orderBy, desc: orderDirection === "descending" }]
    : [];

  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    const next = functionalUpdate(updater, sorting);
    const first = next[0];
    void navigate({
      search: (prev) => ({
        ...prev,
        orderBy: first?.id ?? "",
        orderDirection: first?.desc ? ("descending" as const) : ("ascending" as const),
      }),
      replace: true,
      resetScroll: false,
    });
  };

  const differentYears = rapporten.some(
    (rapport) => new Date(rapport.from).getFullYear() !== new Date(rapport.to).getFullYear(),
  );
  const differentMonths = rapporten.some(
    (rapport) => new Date(rapport.from).getMonth() !== new Date(rapport.to).getMonth(),
  );
  const fromFormatter = useDateFormatter({
    year: differentYears ? "numeric" : undefined,
    month: differentMonths ? "short" : undefined,
    day: "numeric",
  });
  const toFormatter = useDateFormatter({
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  const columns = useMemo(
    () =>
      helper.columns([
        helper.accessor("name", {
          header: "Naam",
          meta: { rowTarget: true },
          cell: ({ row, getValue }) => (
            <Link
              variant="secondary"
              underline
              {...rapportLink(row.original, { locale, schoolyear, school })}
            >
              {getValue() ?? "-"}
            </Link>
          ),
        }),
        helper.display({
          id: "klassen",
          header: () => m["common.fields.classes"](),
          cell: ({ row }) => (
            <div className="flex flex-wrap gap-2">
              {row.original.klassen && row.original.klassen.length > 0 ? (
                row.original.klassen.map((klas) => (
                  <Badge key={klas.id} variant="gray" size="sm">
                    {klas.name}
                  </Badge>
                ))
              ) : (
                <Badge variant="gray" size="sm">
                  Geen klassen
                </Badge>
              )}
            </div>
          ),
        }),
        helper.accessor("from", {
          header: () => m["rapporten.overview.table.date_range"](),
          enableSorting: true,
          meta: { cellClassName: "text-muted-foreground" },
          cell: ({ row }) =>
            `${fromFormatter.format(new Date(row.original.from))} - ${toFormatter.format(new Date(row.original.to))}`,
        }),
        helper.accessor("docent", {
          header: () => m["rapporten.overview.table.docent"](),
          meta: { cellClassName: "text-muted-foreground max-w-[12rem] truncate" },
        }),
        helper.display({
          id: "actions",
          header: "",
          meta: { action: true, headerClassName: "w-0" },
          cell: ({ row }) => (
            <DataTableActions
              actions={[
                {
                  key: "delete",
                  label: m["rapporten.overview.delete.confirm_title"](),
                  icon: <IconMaterialSymbolsDelete className="size-4" />,
                  destructive: true,
                  confirm: {
                    title: m["rapporten.overview.delete.confirm_title"](),
                    message: m["rapporten.overview.delete.confirm_message"]({
                      rapportName: row.original.name ?? "-",
                    }),
                  },
                  onClick: async () => {
                    await submitDeleteRapportFn({ data: { rapportId: row.original.id } });
                    toast.success(m["rapporten.overview.delete.toast_success"]());
                    await router.invalidate();
                  },
                },
              ]}
            />
          ),
        }),
      ]),
    [fromFormatter, toFormatter, submitDeleteRapportFn, router, locale, schoolyear, school],
  );

  return (
    <Page>
      {rapporten.length > 0 && (
        <Page.Header>
          <Page.Title>Rapporten</Page.Title>
          <Page.Header.Actions>
            <Page.Header.MainAction>
              <CreateRapportButton className={"max-sm:w-full"} size="xl">
                Voeg toe
                <IconMaterialSymbolsAdd />
              </CreateRapportButton>
            </Page.Header.MainAction>
          </Page.Header.Actions>
        </Page.Header>
      )}
      {rapporten.length === 0 && <Page.MobileHeader />}
      {rapporten.length === 0 ? (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>Geen rapporten gevonden</EmptyTitle>
            <EmptyDescription>
              Maak een rapport aan om hier resultaten te bekijken.
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <CreateRapportButton className={"max-sm:w-full"} size="xl">
              Voeg rapport toe
              <IconMaterialSymbolsAdd />
            </CreateRapportButton>
          </EmptyContent>
        </Empty>
      ) : (
        <PendingContent>
          {totalCount > RAPPORTEN_OVERVIEW_PAGE_SIZE ? (
            <Page.Container className="pb-4">
              <p className="text-muted-foreground text-sm">
                Er zijn {totalCount} rapporten; er worden maximaal {RAPPORTEN_OVERVIEW_PAGE_SIZE}{" "}
                getoond.
              </p>
            </Page.Container>
          ) : null}
          <DataTable
            alignToContainer
            aria-label="Rapporten"
            columns={columns}
            data={rapporten}
            getRowId={(row) => row.id}
            sorting={sorting}
            onSortingChange={handleSortingChange}
          />
        </PendingContent>
      )}
    </Page>
  );
}

function ReportsIndexError({ error }: { error: unknown }) {
  const params = Route.useParams();
  return <ErrorBoundaryComponent error={error} params={params} />;
}
